import {
  createElement,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { ApiError } from '../api/client';
import type {
  CashbackBank,
  CashbackCategory,
  CashbackProgram,
  CashbackSnapshot,
  Envelope,
} from '../api/contract';
import { useAccountApi, useAppData } from '../app/AppContext';
import { FinanceAppearanceEditor } from '../shared/ui/FinanceAppearanceEditor';
import { EntityColorPicker } from '../shared/ui/EntityColorPicker';
import {
  getFinanceIcon,
  normalizeFinanceIconId,
} from '../shared/ui/financeAppearance';
import {
  bankFormSchema,
  categoryFormSchema,
  currentOffers,
  filterBanks,
  formatCashbackRate,
  formatRateInput,
  getBankMonogram,
  groupCategories,
  parseCashbackRate,
  pluralizeBanks,
  pluralizeCategories,
  sanitizeCashbackRate,
  type CashbackDirectoryMode,
} from './cashback-model';

export const cashbackQueryKey = (workspaceId: string) => ['cashback', workspaceId] as const;

type DraftOffer = { categoryId: string; rate: string };

export function CashbackPage() {
  const { workspace } = useAppData();
  const snapshotQuery = useCashbackSnapshot();
  const [mode, setMode] = useState<CashbackDirectoryMode>('banks');
  const [query, setQuery] = useState('');
  const snapshotMonth = snapshotQuery.data?.month;
  useEffect(() => {
    if (snapshotMonth !== undefined) {
      window.dispatchEvent(
        new CustomEvent('kazna:cashback-month', { detail: { month: snapshotMonth } }),
      );
    }
  }, [snapshotMonth]);

  if (workspace === undefined) return null;
  if (snapshotQuery.isPending) return <CashbackState title="Загружаем кешбэк…" />;
  if (snapshotQuery.isError || snapshotQuery.data === undefined) {
    return <CashbackState title="Не удалось загрузить кешбэк" action="Повторить" onAction={() => snapshotQuery.refetch()} />;
  }

  const snapshot = snapshotQuery.data;
  const banks = filterBanks(snapshot, query);
  const categories = groupCategories(snapshot, query);
  const empty = mode === 'banks' ? banks.length === 0 : categories.length === 0;

  return (
    <section className="cashback-page" aria-label="Кешбэк">
      <label className="cashback-search">
        <Search size={19} aria-hidden />
        <span className="sr-only">Поиск кешбэка</span>
        <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Категория или банк" inputMode="search" />
      </label>
      <div className="cashback-segment" role="tablist" aria-label="Представление кешбэка">
        <button role="tab" aria-selected={mode === 'categories'} className={mode === 'categories' ? 'active' : ''} onClick={() => setMode('categories')}>По категориям</button>
        <button role="tab" aria-selected={mode === 'banks'} className={mode === 'banks' ? 'active' : ''} onClick={() => setMode('banks')}>По банкам</button>
      </div>

      {empty ? (
        <div className="card cashback-empty">
          <strong>{snapshot.banks.length > 0 ? 'Ничего не найдено' : 'Добавьте первый банк'}</strong>
          <p>{snapshot.banks.length > 0 ? 'Измените запрос или переключите представление.' : 'Укажите банк и категории кешбэка на текущий месяц.'}</p>
          {snapshot.banks.length === 0 && <Link className="cashback-primary compact" to="/cashback/banks/new"><Plus size={18} />Добавить банк</Link>}
        </div>
      ) : mode === 'banks' ? (
        banks.map(({ bank, offers }) => (
          <Link className="card cashback-directory-card" key={bank.id} to={`/cashback/banks/${bank.id}`} aria-label={`Открыть банк ${bank.name}`}>
            <div className="cashback-card-header">
              <BankMonogram bank={bank} size={48} />
              <div className="cashback-title-block"><strong>{bank.name}</strong><small>{offers.length > 0 ? `${offers.length} ${pluralizeCategories(offers.length)}` : 'Категории не настроены'}</small></div>
              <ChevronRight size={22} className="cashback-muted" />
            </div>
            {offers.map(offer => {
              const category = snapshot.categories.find(item => item.id === offer.categoryId);
              return category === undefined ? null : (
                <div className="cashback-offer-row" key={offer.categoryId}>
                  <CategoryIcon category={category} size={28} />
                  <strong>{category.name}</strong>
                  <b>{formatCashbackRate(offer.rateBps)}</b>
                </div>
              );
            })}
          </Link>
        ))
      ) : (
        categories.map(group => {
          const topRate = group.offers[0]?.offer.rateBps;
          return (
            <article className="card cashback-directory-card" key={group.category.id}>
              <div className="cashback-card-header">
                <CategoryIcon category={group.category} size={48} />
                <div className="cashback-title-block"><strong>{group.category.name}</strong><small>{group.offers.length} {pluralizeBanks(group.offers.length)}</small></div>
              </div>
              {group.offers.map(({ offer, bank }) => {
                const best = offer.rateBps === topRate;
                return (
                  <Link className="cashback-offer-row" key={bank.id} to={`/cashback/banks/${bank.id}`} aria-label={`${group.category.name}: ${bank.name}, ${formatCashbackRate(offer.rateBps)}`}>
                    <span className={`cashback-rate-circle${best ? ' best' : ''}`}>{formatCashbackRate(offer.rateBps)}</span>
                    <strong>{bank.name}</strong>
                    {best && <em>Лучший</em>}
                  </Link>
                );
              })}
            </article>
          );
        })
      )}
    </section>
  );
}

export function CashbackBankPage() {
  const { bankId = '' } = useParams();
  const snapshotQuery = useCashbackSnapshot();
  if (snapshotQuery.isPending) return <CashbackScreen title="Банк"><CashbackState title="Загружаем банк…" /></CashbackScreen>;
  if (snapshotQuery.isError || snapshotQuery.data === undefined) return <CashbackScreen title="Банк"><CashbackState title="Не удалось загрузить банк" action="Повторить" onAction={() => snapshotQuery.refetch()} /></CashbackScreen>;
  const snapshot = snapshotQuery.data;
  const bank = snapshot.banks.find(item => item.id === bankId);
  if (bank === undefined) return <CashbackScreen title="Банк"><div className="card cashback-empty"><strong>Банк не найден</strong></div></CashbackScreen>;
  const offers = currentOffers(snapshot).filter(offer => offer.bankId === bankId).sort((first, second) => second.rateBps - first.rateBps);
  return (
    <CashbackScreen title="Банк" action={<Link aria-label="Изменить банк" to={`/cashback/banks/${bank.id}/edit`}><Pencil size={20} /></Link>}>
      <Link className="card cashback-bank-identity" to={`/cashback/banks/${bank.id}/edit`} aria-label={`Изменить банк ${bank.name}`}>
        <BankMonogram bank={bank} size={54} />
        <div className="cashback-title-block"><strong>{bank.name}</strong><small>Программа кешбэка</small></div>
        <ChevronRight size={22} className="cashback-muted" />
      </Link>
      <div className="cashback-section-heading"><h2>Кешбэк месяца</h2><p>{offers.length > 0 ? `${offers.length} ${pluralizeCategories(offers.length)}` : 'Категории не настроены'}</p></div>
      <div className="card cashback-bank-offers">
        {offers.length === 0 ? <div className="cashback-empty"><strong>Пока нет категорий</strong><p>Добавьте предложения банка на текущий месяц.</p></div> : offers.map(offer => {
          const category = snapshot.categories.find(item => item.id === offer.categoryId);
          return category === undefined ? null : <div className="cashback-bank-offer" key={offer.categoryId}><CategoryIcon category={category} size={38} /><strong>{category.name}</strong><b>{formatCashbackRate(offer.rateBps)}</b></div>;
        })}
      </div>
      <Link className="cashback-primary" to={`/cashback/banks/${bank.id}/program`}>Настроить категории</Link>
      <Link className="cashback-secondary" to={`/cashback/banks/${bank.id}/edit`}>Изменить банк</Link>
    </CashbackScreen>
  );
}

export function CashbackBankFormPage() {
  const { apiMutation } = useAccountApi();
  const { bankId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspace } = useAppData();
  const snapshotQuery = useCashbackSnapshot();
  const [searchParams, setSearchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#FBBF24');
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');
  const bank = snapshotQuery.data?.banks.find(item => item.id === bankId);
  if (!initialized && snapshotQuery.data !== undefined) {
    if (bank !== undefined) { setName(bank.name); setColor(bank.color); }
    setInitialized(true);
  }
  const mutation = useMutation({
    mutationFn: async () => {
      if (workspace === undefined) throw new Error('Пространство не выбрано');
      const values = bankFormSchema.parse({ name, color });
      return bank === undefined
        ? apiMutation<Envelope<CashbackBank>>(`/workspaces/${workspace.id}/cashback/banks`, { body: values, scope: `cashback:bank:create:${workspace.id}` })
        : apiMutation<Envelope<CashbackBank>>(`/workspaces/${workspace.id}/cashback/banks/${bank.id}`, { method: 'PATCH', body: values, scope: `cashback:bank:update:${workspace.id}:${bank.id}`, version: bank.version });
    },
    onSuccess: async result => {
      if (workspace === undefined) return;
      await queryClient.invalidateQueries({ queryKey: cashbackQueryKey(workspace.id) });
      navigate(bank === undefined ? `/cashback/banks/${result.data.id}/program` : `/cashback/banks/${result.data.id}`, { replace: bank === undefined });
    },
    onError: caught => setError(readError(caught, 'Не удалось сохранить банк')),
  });
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (workspace === undefined || bank === undefined) return;
      await apiMutation(`/workspaces/${workspace.id}/cashback/banks/${bank.id}`, { method: 'DELETE', scope: `cashback:bank:delete:${workspace.id}:${bank.id}`, version: bank.version });
    },
    onSuccess: async () => {
      if (workspace !== undefined) await queryClient.invalidateQueries({ queryKey: cashbackQueryKey(workspace.id) });
      navigate('/cashback', { replace: true });
    },
    onError: caught => setError(readError(caught, 'Не удалось удалить банк')),
  });
  const showDelete = searchParams.get('overlay') === 'delete-bank';
  const closeDelete = () => { const next = new URLSearchParams(searchParams); next.delete('overlay'); setSearchParams(next, { replace: true }); };
  if (snapshotQuery.isPending) return <CashbackScreen title={bankId === undefined ? 'Новый банк' : 'Изменить банк'}><CashbackState title="Загружаем банк…" /></CashbackScreen>;
  if (snapshotQuery.isError || snapshotQuery.data === undefined) return <CashbackScreen title={bankId === undefined ? 'Новый банк' : 'Изменить банк'}><CashbackState title="Не удалось загрузить банк" action="Повторить" onAction={() => snapshotQuery.refetch()} /></CashbackScreen>;
  if (bankId !== undefined && bank === undefined) return <CashbackScreen title="Изменить банк"><div className="cashback-empty"><strong>Банк не найден</strong></div></CashbackScreen>;
  const pending = mutation.isPending || deleteMutation.isPending;
  return (
    <CashbackScreen title={bankId === undefined ? 'Новый банк' : 'Изменить банк'}>
      <div className="cashback-bank-preview"><BankMonogram bank={{ name: name.trim(), color } as CashbackBank} size={72} /><strong>{name.trim() || 'Название банка'}</strong><small>Буква и цвет используются вместо логотипа</small></div>
      <label className="cashback-field"><span>Название</span><input aria-label="Название банка" value={name} onChange={event => setName(event.target.value)} maxLength={60} autoCapitalize="words" placeholder="Например, Т-Банк" /></label>
      <EntityColorPicker value={color} onChange={setColor} disabled={pending} />
      {error.length > 0 && <p className="cashback-error" role="alert">{error}</p>}
      <button className="cashback-primary" disabled={pending} onClick={() => mutation.mutate()}><Check size={20} />{mutation.isPending ? 'Сохраняем…' : 'Сохранить банк'}</button>
      {bank !== undefined && <button className="cashback-danger" disabled={pending} onClick={() => { const next = new URLSearchParams(searchParams); next.set('overlay', 'delete-bank'); setSearchParams(next); }}><Trash2 size={19} />Удалить банк</button>}
      {showDelete && <CashbackOverlay title="Удалить банк?" onClose={closeDelete}><p>Банк и его категории кешбэка будут удалены из этого пространства.</p><div className="cashback-confirm-actions"><button onClick={closeDelete}>Отмена</button><button className="danger" onClick={() => deleteMutation.mutate()}>Удалить</button></div></CashbackOverlay>}
    </CashbackScreen>
  );
}

export function CashbackProgramPage() {
  const { apiMutation } = useAccountApi();
  const { bankId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { workspace } = useAppData();
  const snapshotQuery = useCashbackSnapshot();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drafts, setDrafts] = useState<DraftOffer[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');
  const [customName, setCustomName] = useState('');
  const [customColor, setCustomColor] = useState('#A855F7');
  const [customIcon, setCustomIcon] = useState('other');
  const [customError, setCustomError] = useState('');
  const snapshot = snapshotQuery.data;
  const bank = snapshot?.banks.find(item => item.id === bankId);
  const program = snapshot?.programs.find(item => item.bankId === bankId && item.month === snapshot.month);
  if (snapshot !== undefined && !initialized) {
    setDrafts((program?.offers ?? []).slice().sort((first, second) => second.rateBps - first.rateBps).map(offer => ({ categoryId: offer.categoryId, rate: formatRateInput(offer.rateBps) })));
    setInitialized(true);
  }
  const selected = useMemo(() => new Set(drafts.map(draft => draft.categoryId)), [drafts]);
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (workspace === undefined || snapshot === undefined) throw new Error('Пространство не выбрано');
      const offers = drafts.map(draft => ({ categoryId: draft.categoryId, rateBps: parseCashbackRate(draft.rate) }));
      const path = `/workspaces/${workspace.id}/cashback/banks/${bankId}/programs/${snapshot.month}`;
      const options = program === undefined
        ? { method: 'PUT' as const, body: { offers }, scope: `cashback:program:create:${workspace.id}:${bankId}:${snapshot.month}`, createOnly: true as const }
        : { method: 'PUT' as const, body: { offers }, scope: `cashback:program:update:${workspace.id}:${bankId}:${snapshot.month}`, version: program.version };
      return apiMutation<Envelope<CashbackProgram>>(path, options);
    },
    onSuccess: async () => {
      if (workspace !== undefined) await queryClient.invalidateQueries({ queryKey: cashbackQueryKey(workspace.id) });
      navigate(`/cashback/banks/${bankId}`);
    },
    onError: async caught => {
      if (workspace !== undefined && caught instanceof ApiError && caught.status === 412) await queryClient.invalidateQueries({ queryKey: cashbackQueryKey(workspace.id) });
      setError(readError(caught, 'Не удалось сохранить категории'));
    },
  });
  const categoryMutation = useMutation({
    mutationFn: async () => {
      if (workspace === undefined) throw new Error('Пространство не выбрано');
      const values = categoryFormSchema.parse({ name: customName, color: customColor });
      return apiMutation<Envelope<CashbackCategory>>(`/workspaces/${workspace.id}/cashback/categories`, { body: { ...values, icon: normalizeFinanceIconId(customIcon, 'other') }, scope: `cashback:category:create:${workspace.id}` });
    },
    onSuccess: async result => {
      if (workspace !== undefined) await queryClient.invalidateQueries({ queryKey: cashbackQueryKey(workspace.id) });
      setDrafts(current => [...current, { categoryId: result.data.id, rate: '' }]);
      setCustomName(''); setCustomColor('#A855F7'); setCustomIcon('other'); setCustomError(''); closeOverlay();
    },
    onError: caught => setCustomError(readError(caught, 'Не удалось создать категорию')),
  });
  const overlay = searchParams.get('overlay');
  const showCustomAppearance =
    overlay === 'custom-category' && searchParams.get('appearance') === '1';
  const openOverlay = (name: string) => { const next = new URLSearchParams(searchParams); next.set('overlay', name); setSearchParams(next); };
  const closeOverlay = () => { const next = new URLSearchParams(searchParams); next.delete('overlay'); next.delete('appearance'); setSearchParams(next, { replace: true }); };
  const openCustomAppearance = () => {
    const next = new URLSearchParams(searchParams);
    next.set('appearance', '1');
    setSearchParams(next, {
      state: { ...(location.state as object | null), appearanceFromForm: true },
    });
  };
  const closeCustomAppearance = () => {
    if ((location.state as { appearanceFromForm?: boolean } | null)?.appearanceFromForm) {
      navigate(-1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('appearance');
    setSearchParams(next, { replace: true, state: location.state });
  };
  if (snapshotQuery.isPending) return <CashbackScreen title="Кешбэк банка"><CashbackState title="Загружаем категории…" /></CashbackScreen>;
  if (snapshotQuery.isError || snapshot === undefined) return <CashbackScreen title="Кешбэк банка"><CashbackState title="Не удалось загрузить категории" action="Повторить" onAction={() => snapshotQuery.refetch()} /></CashbackScreen>;
  if (bank === undefined) return <CashbackScreen title="Кешбэк банка"><div className="cashback-empty"><strong>Банк не найден</strong></div></CashbackScreen>;
  const toggleCategory = (categoryId: string, enabled: boolean) => {
    setDrafts(current => enabled
      ? current.some(draft => draft.categoryId === categoryId)
        ? current
        : [...current, { categoryId, rate: '' }]
      : current.filter(draft => draft.categoryId !== categoryId));
    setError('');
  };
  return (
    <CashbackScreen title="Кешбэк банка" action={<button aria-label="Сохранить категории" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}><Check size={21} /></button>}>
      <div className="cashback-program-bank"><BankMonogram bank={bank} size={46} /><div className="cashback-title-block"><strong>{bank.name}</strong><small>Текущий месяц</small></div></div>
      <p className="cashback-instruction">Выберите категории из готового списка и укажите проценты.</p>
      {drafts.map(draft => {
        const category = snapshot.categories.find(item => item.id === draft.categoryId);
        return category === undefined ? null : (
          <div className="cashback-offer-editor" key={draft.categoryId}>
            <CategoryIcon category={category} size={38} /><strong>{category.name}</strong>
            <input aria-label={`Процент кешбэка: ${category.name}`} value={draft.rate} onChange={event => setDrafts(current => current.map(item => item.categoryId === draft.categoryId ? { ...item, rate: sanitizeCashbackRate(event.target.value) } : item))} inputMode="decimal" maxLength={6} placeholder="0" />
            <span>%</span><button aria-label={`Удалить категорию ${category.name}`} onClick={() => setDrafts(current => current.filter(item => item.categoryId !== draft.categoryId))}><Trash2 size={20} /></button>
          </div>
        );
      })}
      <div className="cashback-catalog-actions"><span>Готовые категории</span><button className="cashback-secondary select" onClick={() => openOverlay('category-picker')}>Выбрать из списка<ChevronDown size={20} /></button><button className="cashback-link-button" onClick={() => { setCustomError(''); openOverlay('custom-category'); }}><Plus size={18} />Создать свою категорию</button></div>
      {error.length > 0 && <p className="cashback-error boxed" role="alert">{error}</p>}
      {overlay === 'category-picker' && <CashbackOverlay title="Готовые категории" onClose={closeOverlay} tall>{snapshot.categories.map(category => { const enabled = selected.has(category.id); return <label className="cashback-category-choice" key={category.id}><CategoryIcon category={category} size={36} /><strong>{category.name}</strong><input type="checkbox" role="switch" aria-label={`Использовать категорию кешбэка: ${category.name}`} checked={enabled} onChange={event => toggleCategory(category.id, event.target.checked)} /></label>; })}</CashbackOverlay>}
      {overlay === 'custom-category' && <CashbackOverlay title="Своя категория" onClose={closeOverlay}><label className="cashback-field"><span className="sr-only">Название своей категории</span><input autoFocus={!showCustomAppearance} aria-label="Название своей категории" value={customName} onChange={event => setCustomName(event.target.value)} maxLength={60} placeholder="Название категории" /></label><button type="button" className="appearance-picker" aria-label="Открыть выбор иконки и цвета" onClick={openCustomAppearance}><span style={{ background: customColor }}>{createElement(getFinanceIcon(customIcon, 'other'), { size: 28 })}</span><span><strong>Иконка и цвет</strong><small>Нажмите, чтобы изменить</small></span><ChevronRight size={21} /></button>{customError.length > 0 && <p className="cashback-error" role="alert">{customError}</p>}<button className="cashback-primary" disabled={categoryMutation.isPending} onClick={() => categoryMutation.mutate()}>{categoryMutation.isPending ? 'Создаём…' : 'Создать категорию'}</button></CashbackOverlay>}
      {showCustomAppearance && <FinanceAppearanceEditor color={customColor} icon={customIcon} onColorChange={setCustomColor} onIconChange={setCustomIcon} onDone={closeCustomAppearance} />}
    </CashbackScreen>
  );
}

function useCashbackSnapshot() {
  const { apiGet } = useAccountApi();
  const { workspace } = useAppData();
  return useQuery({
    queryKey: cashbackQueryKey(workspace?.id ?? 'none'),
    enabled: workspace !== undefined,
    queryFn: () => apiGet<Envelope<CashbackSnapshot>>(`/workspaces/${workspace!.id}/cashback`).then(result => result.data),
    retry: false,
  });
}

function CashbackScreen({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  const navigate = useNavigate();
  const goBack = () => {
    const historyIndex: unknown = window.history.state?.idx;
    if (typeof historyIndex === 'number' && historyIndex > 0) navigate(-1);
    else navigate('/cashback', { replace: true });
  };
  return <section className="cashback-screen"><header className="cashback-screen-header"><button aria-label="Назад" onClick={goBack}><ChevronLeft size={22} /></button><h1>{title}</h1><span>{action}</span></header>{children}</section>;
}

function CashbackState({ title, action, onAction }: { title: string; action?: string; onAction?: () => void }) {
  return <div className="card cashback-empty cashback-state" role="status"><strong>{title}</strong>{action !== undefined && <button className="cashback-secondary compact" onClick={onAction}>{action}</button>}</div>;
}

function BankMonogram({ bank, size }: { bank: Pick<CashbackBank, 'name' | 'color'>; size: number }) {
  return <span className="cashback-monogram" style={{ '--cashback-size': `${size}px`, backgroundColor: bank.color } as CSSProperties}>{getBankMonogram(bank.name)}</span>;
}

function CategoryIcon({ category, size }: { category: CashbackCategory; size: number }) {
  const Icon = getFinanceIcon(category.icon, 'other');
  return <span className="cashback-category-icon" style={{ '--cashback-size': `${size}px`, backgroundColor: category.color } as CSSProperties}>{createElement(Icon, { size: size * 0.48, 'aria-hidden': true })}</span>;
}

function CashbackOverlay({ title, onClose, tall = false, children }: { title: string; onClose: () => void; tall?: boolean; children: ReactNode }) {
  return <div className="cashback-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}><section className={`cashback-sheet${tall ? ' tall' : ''}`} role="dialog" aria-modal="true" aria-label={title}><header><h2>{title}</h2><button aria-label="Закрыть" onClick={onClose}><X size={23} /></button></header><div className="cashback-sheet-body">{children}</div></section></div>;
}

function readError(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError || caught instanceof Error) return caught.message;
  return fallback;
}
